/**
 * A deliberately small Markdown renderer.
 *
 * Notes and comments are family-written prose and shopping lists, so we only
 * support what people actually type. Input is HTML-escaped before any markup
 * is generated, and link hrefs are protocol-checked, so the output is safe to
 * inject with dangerouslySetInnerHTML without pulling in a sanitiser.
 *
 * Supports: headings, bold, italic, inline code, fenced code, links,
 * bullet/numbered lists, task lists, blockquotes, horizontal rules.
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function safeHref(url: string): string | null {
  const trimmed = url.trim();
  return /^(https?:\/\/|mailto:|\/)/i.test(trimmed) ? trimmed : null;
}

/**
 * Inline formatting for already-escaped text. Splitting on backticks means
 * code spans keep their contents verbatim without needing placeholders.
 */
function inline(text: string): string {
  return text
    .split(/`([^`]+)`/g)
    .map((part, index) => (index % 2 === 1 ? `<code>${part}</code>` : emphasis(part)))
    .join("");
}

function emphasis(text: string): string {
  let out = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, label: string, url: string) => {
    const href = safeHref(url);
    return href ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>` : match;
  });

  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*\s][^*]*)\*/g, "$1<em>$2</em>");
  out = out.replace(/~~([^~]+)~~/g, "<del>$1</del>");

  return out;
}

export function renderMarkdown(source: string): string {
  const lines = escapeHtml(source ?? "").split(/\r?\n/);
  const html: string[] = [];

  let listType: "ul" | "ol" | null = null;
  let paragraph: string[] = [];
  let inCodeBlock = false;
  let codeLines: string[] = [];

  const closeList = () => {
    if (listType) {
      html.push(`</${listType}>`);
      listType = null;
    }
  };

  const closeParagraph = () => {
    if (paragraph.length > 0) {
      html.push(`<p>${inline(paragraph.join(" "))}</p>`);
      paragraph = [];
    }
  };

  const openList = (type: "ul" | "ol") => {
    if (listType !== type) {
      closeList();
      html.push(`<${type}>`);
      listType = type;
    }
  };

  for (const line of lines) {
    if (/^\s*```/.test(line)) {
      if (inCodeBlock) {
        html.push(`<pre><code>${codeLines.join("\n")}</code></pre>`);
        codeLines = [];
        inCodeBlock = false;
      } else {
        closeParagraph();
        closeList();
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }

    if (line.trim() === "") {
      closeParagraph();
      closeList();
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      closeParagraph();
      closeList();
      const level = heading[1]!.length;
      html.push(`<h${level}>${inline(heading[2]!)}</h${level}>`);
      continue;
    }

    if (/^\s*(---+|\*\*\*+)\s*$/.test(line)) {
      closeParagraph();
      closeList();
      html.push("<hr />");
      continue;
    }

    // '>' has already been escaped to &gt; by this point.
    const quote = /^&gt;\s?(.*)$/.exec(line);
    if (quote) {
      closeParagraph();
      closeList();
      html.push(`<blockquote>${inline(quote[1]!)}</blockquote>`);
      continue;
    }

    // Task list items render as (disabled) checkboxes — shopping lists live here.
    const task = /^\s*[-*]\s+\[([ xX])\]\s+(.*)$/.exec(line);
    if (task) {
      closeParagraph();
      openList("ul");
      const checked = task[1]!.toLowerCase() === "x" ? " checked" : "";
      html.push(
        `<li class="task"><input type="checkbox" disabled${checked} /> <span>${inline(task[2]!)}</span></li>`,
      );
      continue;
    }

    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      closeParagraph();
      openList("ul");
      html.push(`<li>${inline(bullet[1]!)}</li>`);
      continue;
    }

    const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (numbered) {
      closeParagraph();
      openList("ol");
      html.push(`<li>${inline(numbered[1]!)}</li>`);
      continue;
    }

    paragraph.push(line.trim());
  }

  if (inCodeBlock && codeLines.length > 0) {
    html.push(`<pre><code>${codeLines.join("\n")}</code></pre>`);
  }
  closeParagraph();
  closeList();

  return html.join("\n");
}
